import * as React from "react";
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import type { ApiClient } from "@platform/api-client";
import type { AuthSession, UserWithContext as User } from "@platform/types";

const ApiClientContext = React.createContext<ApiClient | null>(null);

export function ApiClientProvider({ client, children }: { client: ApiClient; children: React.ReactNode }) {
  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

export function useApiClient(): ApiClient {
  const ctx = React.useContext(ApiClientContext);
  if (!ctx) throw new Error("useApiClient must be used within <ApiClientProvider>");
  return ctx;
}

// ── Auth ─────────────────────────────────────────────────────────────────────

export function useCurrentUser(options?: Partial<UseQueryOptions<User>>) {
  const client = useApiClient();
  return useQuery({ queryKey: ["auth", "me"], queryFn: () => client.auth.me(), ...options });
}

export function useLogin() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) => client.auth.login(input),
    onSuccess: (session: AuthSession) => { qc.setQueryData(["auth", "me"], session.user); },
  });
}

export function useRegister() {
  const client = useApiClient();
  // No onSuccess caching a session here anymore — register no longer
  // returns one (see auth.service.ts's register for why: password
  // signups must verify before they can log in at all). Nothing to
  // cache until the user actually logs in through the gated path.
  return useMutation({
    mutationFn: (input: { email: string; password: string; name: string }) => client.auth.register(input),
  });
}

export function useForgotPassword() {
  const client = useApiClient();
  return useMutation({ mutationFn: (email: string) => client.auth.forgotPassword(email) });
}

export function useResetPassword() {
  const client = useApiClient();
  return useMutation({
    mutationFn: ({ token, newPassword }: { token: string; newPassword: string }) => client.auth.resetPassword(token, newPassword),
  });
}

export function useVerifyEmail() {
  const client = useApiClient();
  return useMutation({ mutationFn: (token: string) => client.auth.verifyEmail(token) });
}

export function useResendVerification() {
  const client = useApiClient();
  return useMutation({ mutationFn: (email: string) => client.auth.resendVerification(email) });
}

export function useGoogleLogin() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (idToken: string) => client.auth.google(idToken),
    onSuccess: (session: AuthSession) => { qc.setQueryData(["auth", "me"], session.user); },
  });
}

export function useLogout() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => client.auth.logout(),
    onSuccess: () => qc.clear(),
  });
}

// ── Enterprise — users ────────────────────────────────────────────────────────

export function useEnterpriseUsers() {
  const client = useApiClient();
  return useQuery({ queryKey: ["enterprise", "users"], queryFn: () => client.enterprise.listUsers() });
}

export function useApproveUser() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => client.enterprise.approveUser(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["enterprise", "users"] }),
  });
}

export function useDeclineUser() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => client.enterprise.declineUser(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["enterprise", "users"] }),
  });
}

export function useResetUserPassword() {
  const client = useApiClient();
  return useMutation({ mutationFn: (userId: string) => client.enterprise.resetPassword(userId) });
}

export function useInviteUser() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof client.enterprise.inviteUser>[0]) =>
      client.enterprise.inviteUser(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["enterprise", "users"] }),
  });
}

// ── Enterprise — roles ────────────────────────────────────────────────────────

export function useEnterpriseRoles() {
  const client = useApiClient();
  return useQuery({ queryKey: ["enterprise", "roles"], queryFn: () => client.enterprise.listRoles() });
}

// ── Enterprise — modules ──────────────────────────────────────────────────────

export function useEnterpriseModules() {
  const client = useApiClient();
  return useQuery({ queryKey: ["enterprise", "modules"], queryFn: () => client.enterprise.listModules() });
}

export function useToggleModule() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ moduleId, enabled }: { moduleId: string; enabled: boolean }) =>
      client.enterprise.toggleModule(moduleId, enabled),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["enterprise", "modules"] }),
  });
}

// ── Enterprise — settings ─────────────────────────────────────────────────────

export function useEnterpriseSettings() {
  const client = useApiClient();
  return useQuery({ queryKey: ["enterprise", "settings"], queryFn: () => client.enterprise.getSettings() });
}

export function useUpdateEnterpriseSettings() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name?: string }) => client.enterprise.updateSettings(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["enterprise", "settings"] }),
  });
}

// ── Profile ───────────────────────────────────────────────────────────────────

export function useProfile() {
  const client = useApiClient();
  return useQuery({ queryKey: ["profile"], queryFn: () => client.profile.get() });
}

export function useUpdateProfile() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof client.profile.update>[0]) => client.profile.update(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["profile"] });
      qc.invalidateQueries({ queryKey: ["auth", "me"] });
    },
  });
}

export function useChangePassword() {
  const client = useApiClient();
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      client.profile.changePassword(input),
  });
}

export function useSessions() {
  const client = useApiClient();
  return useQuery({ queryKey: ["profile", "sessions"], queryFn: () => client.profile.listSessions() });
}

export function useRevokeSession() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => client.profile.revokeSession(sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profile", "sessions"] }),
  });
}

export function useRevokeAllOtherSessions() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (currentSessionId: string) => client.profile.revokeAllOtherSessions(currentSessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["profile", "sessions"] }),
  });
}

// ── Platform ──────────────────────────────────────────────────────────────────

export function usePlatformEnterprises() {
  const client = useApiClient();
  return useQuery({ queryKey: ["platform", "enterprises"], queryFn: () => client.platform.listEnterprises() });
}

export function useSuspendEnterprise() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.platform.suspendEnterprise(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "enterprises"] }),
  });
}

export function useReinstateEnterprise() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.platform.reinstateEnterprise(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "enterprises"] }),
  });
}

export function useCreateEnterprise() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof client.platform.createEnterprise>[0]) =>
      client.platform.createEnterprise(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "enterprises"] }),
  });
}

export function usePlatformUsers() {
  const client = useApiClient();
  return useQuery({ queryKey: ["platform", "users"], queryFn: () => client.platform.listUsers() });
}

export function useCreatePlatformUser() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; email: string; jobTitle?: string }) => client.platform.createUser(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "users"] }),
  });
}

export function useResetUserPasswordPlatform() {
  const client = useApiClient();
  return useMutation({ mutationFn: (id: string) => client.platform.resetUserPassword(id) });
}

export function useSystemHealth() {
  const client = useApiClient();
  return useQuery({
    queryKey: ["platform", "health"],
    queryFn: () => client.platform.health(),
    refetchInterval: 30_000,
  });
}

export function useAuditLog() {
  const client = useApiClient();
  return useQuery({ queryKey: ["platform", "audit"], queryFn: () => client.platform.auditLog() });
}

// ── Enterprise — dashboard ────────────────────────────────────────────────────

export function useEnterpriseDashboard() {
  const client = useApiClient();
  return useQuery({
    queryKey: ["enterprise", "dashboard"],
    queryFn: () => client.enterprise.dashboard(),
    refetchInterval: 60_000,
  });
}

// ── Platform — roles ──────────────────────────────────────────────────────────

export function usePlatformRoles() {
  const client = useApiClient();
  return useQuery({ queryKey: ["platform", "roles"], queryFn: () => client.platform.listRoles() });
}

export function useCreateRole() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { key: string; name: string; description?: string; moduleId?: string; individuallyAssignable?: boolean }) =>
      client.platform.createRole(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "roles"] }),
  });
}

export function useUpdateRole() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; name?: string; description?: string; permissions?: Record<string, Record<string, boolean>>; individuallyAssignable?: boolean }) =>
      client.platform.updateRole(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "roles"] }),
  });
}

export function useDeleteRole() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.platform.deleteRole(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "roles"] }),
  });
}

// ── Enterprise — single user ───────────────────────────────────────────────────

export function useEnterpriseUser(userId: string | null) {
  const client = useApiClient();
  return useQuery({
    queryKey: ["enterprise", "users", userId],
    queryFn: () => client.enterprise.getUser(userId!),
    enabled: !!userId,
  });
}

export function useUnlockUser() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => client.enterprise.unlockUser(userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["enterprise", "users"] }),
  });
}

export function useAssignRoles() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleIds }: { userId: string; roleIds: string[] }) =>
      client.enterprise.assignRoles(userId, roleIds),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["enterprise", "users"] });
      qc.invalidateQueries({ queryKey: ["enterprise", "users", vars.userId] });
    },
  });
}

// ── Platform — single user ────────────────────────────────────────────────────

export function usePlatformUser(userId: string | null) {
  const client = useApiClient();
  return useQuery({
    queryKey: ["platform", "users", userId],
    queryFn: () => client.platform.getUser(userId!),
    enabled: !!userId,
  });
}

export function useUnlockPlatformUser() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.platform.unlockUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "users"] }),
  });
}

export function useSetPlatformRole() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, platformRole }: { id: string; platformRole: "platform_admin" | "support" | null }) =>
      client.platform.setPlatformRole(id, platformRole),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "users"] }),
  });
}

// ── Platform — single enterprise ──────────────────────────────────────────────

export function usePlatformEnterprise(id: string | null) {
  const client = useApiClient();
  return useQuery({
    queryKey: ["platform", "enterprises", id],
    queryFn: () => client.platform.getEnterprise(id!),
    enabled: !!id,
  });
}

export function usePlatformEnterpriseUsers(id: string | null) {
  const client = useApiClient();
  return useQuery({
    queryKey: ["platform", "enterprises", id, "users"],
    queryFn: () => client.platform.getEnterpriseUsers(id!),
    enabled: !!id,
  });
}

// ── Platform — user mutations ─────────────────────────────────────────────────

export function useDeletePlatformUser() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.platform.deleteUser(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "users"] }),
  });
}

export function useAddToEnterprise() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, enterpriseId, roleIds }: { userId: string; enterpriseId: string; roleIds: string[] }) =>
      client.platform.addToEnterprise(userId, enterpriseId, roleIds),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["platform", "users", vars.userId] });
      qc.invalidateQueries({ queryKey: ["platform", "users"] });
    },
  });
}

export function useRemoveEnterpriseMembership() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, enterpriseId }: { userId: string; enterpriseId: string }) =>
      client.platform.removeEnterpriseMembership(userId, enterpriseId),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["platform", "users", vars.userId] });
      qc.invalidateQueries({ queryKey: ["platform", "users"] });
    },
  });
}

export function useAssignPlatformEnterpriseRoles() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, enterpriseId, roleIds }: { userId: string; enterpriseId: string; roleIds: string[] }) =>
      client.platform.assignEnterpriseRoles(userId, enterpriseId, roleIds),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ["platform", "users", vars.userId] }),
  });
}

// Platform-level roles — no enterprise. Assignable to any user by a
// platform admin, independent of enterprise membership.
export function useAssignPlatformRoles() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleIds }: { userId: string; roleIds: string[] }) =>
      client.platform.assignPlatformRoles(userId, roleIds),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ["platform", "users", vars.userId] }),
  });
}

// ── Platform — enterprise sub-resource hooks ──────────────────────────────────

export function usePlatformEnterpriseRoles(id: string | null) {
  const client = useApiClient();
  return useQuery({
    queryKey: ["platform", "enterprises", id, "roles"],
    queryFn: () => client.platform.getEnterpriseRoles(id!),
    enabled: !!id,
  });
}

export function usePlatformEnterpriseModules(id: string | null) {
  const client = useApiClient();
  return useQuery({
    queryKey: ["platform", "enterprises", id, "modules"],
    queryFn: () => client.platform.getEnterpriseModules(id!),
    enabled: !!id,
  });
}

export function useTogglePlatformEnterpriseModule() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, moduleId, enabled }: { id: string; moduleId: string; enabled: boolean }) =>
      client.platform.toggleEnterpriseModule(id, moduleId, enabled),
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ["platform", "enterprises", vars.id, "modules"] }),
  });
}

export function useUpdatePlatformEnterpriseSettings() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; name?: string; allowedDomains?: string[] }) =>
      client.platform.updateEnterpriseSettings(id, input),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["platform", "enterprises", vars.id] });
      qc.invalidateQueries({ queryKey: ["platform", "enterprises"] });
    },
  });
}

// ── SSO hooks ─────────────────────────────────────────────────────────────────

export function useSsoConfig(enterpriseId: string | null) {
  const client = useApiClient();
  return useQuery({
    queryKey: ["sso", "config", enterpriseId],
    queryFn: () => client.sso.getConfig(enterpriseId!),
    enabled: !!enterpriseId,
  });
}

export function useSaveSsoConfig() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ enterpriseId, ...input }: { enterpriseId: string; protocol: "oidc" | "saml"; providerName: string; clientId?: string; clientSecret?: string; domainHint?: string; metadataXml?: string; issuerUrl?: string }) =>
      client.sso.saveConfig(enterpriseId, input),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["sso", "config", vars.enterpriseId] }),
  });
}

export function useSetSsoStatus() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ enterpriseId, status }: { enterpriseId: string; status: "active" | "disabled" }) =>
      client.sso.setStatus(enterpriseId, status),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["sso", "config", vars.enterpriseId] }),
  });
}

export function useTestSsoConnection() {
  const client = useApiClient();
  return useMutation({
    mutationFn: (enterpriseId: string) => client.sso.testConnection(enterpriseId),
  });
}

export function useDeleteSsoConfig() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enterpriseId: string) => client.sso.deleteConfig(enterpriseId),
    onSuccess: (_d, enterpriseId) => qc.invalidateQueries({ queryKey: ["sso", "config", enterpriseId] }),
  });
}

// ---------------------------------------------------------------------------
// Feedback
// ---------------------------------------------------------------------------

export function useSubmitFeedback() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { type: "feedback" | "bug"; title: string; description: string; draftId?: string }) =>
      client.feedback.submit(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feedback", "mine"] }),
  });
}

export function useMyFeedback() {
  const client = useApiClient();
  return useQuery({ queryKey: ["feedback", "mine"], queryFn: () => client.feedback.mine() });
}

export function useAllFeedback() {
  const client = useApiClient();
  return useQuery({ queryKey: ["feedback", "all"], queryFn: () => client.feedback.all() });
}

export function useUpdateFeedbackStatus() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status, reviewerNote }: { id: string; status: "open" | "under_review" | "resolved" | "closed"; reviewerNote?: string }) =>
      client.feedback.updateStatus(id, { status, reviewerNote }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["feedback", "all"] }),
  });
}

// ---------------------------------------------------------------------------
// Platform dashboard reaching into ConfigIQ's data (via the /v1/configiq/*
// proxy every ConfigIQ page already uses — see api-client's configiq
// section).
// ---------------------------------------------------------------------------

export function useTopUsedTasks(windowDays?: number) {
  const client = useApiClient();
  return useQuery({
    queryKey: ["configiq", "top-tasks", windowDays ?? "all"],
    queryFn: () => client.configiq.topTasks(windowDays),
  });
}

// ---------------------------------------------------------------------------
// Email settings (platform admin)
// ---------------------------------------------------------------------------

export function useEmailSettings() {
  const client = useApiClient();
  return useQuery({ queryKey: ["platform", "email-settings"], queryFn: () => client.platform.getEmailSettings() });
}

export function useUpdateEmailSettings() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { provider?: string; apiKey?: string; fromEmail?: string; fromName?: string; enabled?: boolean; monthlyLimit?: number | null }) =>
      client.platform.updateEmailSettings(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "email-settings"] }),
  });
}

export function useEmailStats() {
  const client = useApiClient();
  return useQuery({ queryKey: ["platform", "email-stats"], queryFn: () => client.platform.getEmailStats() });
}

// ---------------------------------------------------------------------------
// Notifications (platform admin + support)
// ---------------------------------------------------------------------------

export function useNotifications() {
  const client = useApiClient();
  return useQuery({
    queryKey: ["notifications"],
    queryFn: () => client.notifications.list(),
    // Cheap enough (50-row query) to poll for a bell icon that should
    // feel roughly live without needing a websocket for one small
    // feature — refetch matches this session's own established polling
    // style elsewhere, not a new pattern invented for this.
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.notifications.markRead(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useMarkAllNotificationsRead() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => client.notifications.markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

// ---------------------------------------------------------------------------
// File storage — own files only, every hook. No admin-override variant;
// "access restricted only to them" is the actual backend behavior, not
// just a UI choice, so there's nothing to build a bypassed version of.
// ---------------------------------------------------------------------------

export function useFiles() {
  const client = useApiClient();
  return useQuery({ queryKey: ["files", "list"], queryFn: () => client.files.list() });
}

export function useUploadFile() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { fileName: string; mimeType: string; contentBase64: string; folderPath?: string }) =>
      client.files.upload(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["files", "list"] }),
  });
}

export function useDeleteFile() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.files.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["files", "list"] }),
  });
}

export function useFileDownloadUrl() {
  const client = useApiClient();
  // Returns a mutationFn that fetches the file as a blob with the same
  // JWT the rest of the client uses, then triggers a browser save-as.
  // The download itself is authenticated (platform-base's /v1/files/:id/
  // download enforces JWT + ownership) so only the file's own uploader
  // can trigger this — no public MinIO URL involved at all.
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.files.downloadBlob(id);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const disposition = res.headers.get("Content-Disposition") ?? "";
      const match = disposition.match(/filename="([^"]+)"/);
      const fileName = match?.[1] ?? "download";
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    },
  });
}

export function useStorageSettings() {
  const client = useApiClient();
  return useQuery({ queryKey: ["platform", "storage-settings"], queryFn: () => client.storageSettings.get() });
}

export function useUpdateStorageSettings() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (defaultQuotaBytes: number) => client.storageSettings.update(defaultQuotaBytes),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["platform", "storage-settings"] }),
  });
}

// ---------------------------------------------------------------------------
// Scheduler — platform or enterprise admin only (enforced by the backend's
// requireEnterpriseAdmin, not re-checked here — these hooks assume the
// caller already knows they're allowed to see this page).
// ---------------------------------------------------------------------------

export function useScheduledJobs() {
  const client = useApiClient();
  return useQuery({ queryKey: ["scheduler", "jobs"], queryFn: () => client.scheduler.list() });
}

export function useScheduledJob(id: string | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: ["scheduler", "jobs", id],
    queryFn: () => client.scheduler.get(id!),
    enabled: Boolean(id),
  });
}

export function useCreateScheduledJob() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof client.scheduler.create>[0]) => client.scheduler.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scheduler", "jobs"] }),
  });
}

export function useUpdateScheduledJob() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string } & Parameters<typeof client.scheduler.update>[1]) =>
      client.scheduler.update(id, input),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["scheduler", "jobs"] });
      qc.invalidateQueries({ queryKey: ["scheduler", "jobs", vars.id] });
    },
  });
}

export function usePauseScheduledJob() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      client.scheduler.update(id, { enabled }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scheduler", "jobs"] }),
  });
}

export function useDeleteScheduledJob() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.scheduler.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["scheduler", "jobs"] }),
  });
}

export function useTriggerScheduledJob() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => client.scheduler.trigger(id),
    onSuccess: (_d, id) => qc.invalidateQueries({ queryKey: ["scheduler", "jobs", id] }),
  });
}

export function useSchedulerRunHistory() {
  const client = useApiClient();
  return useQuery({ queryKey: ["scheduler", "runs", "all"], queryFn: () => client.scheduler.runs() });
}

// ---------------------------------------------------------------------------
// Storage quota management — enterprise and user level
// ---------------------------------------------------------------------------

export function useEnterpriseStorage(enterpriseId: string | null | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: ["platform", "enterprise", enterpriseId, "storage"],
    queryFn: () => client.platform.enterpriseStorage(enterpriseId!),
    enabled: Boolean(enterpriseId),
  });
}

export function useUpdateEnterpriseStorage() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: { id: string; userStorageLimitBytes?: number | null; totalStorageLimitBytes?: number | null }) =>
      client.platform.updateEnterpriseStorage(id, input),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["platform", "enterprise", vars.id, "storage"] }),
  });
}

export function useSetDefaultEnterprise() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, enterpriseId }: { userId: string; enterpriseId: string }) =>
      client.platform.setDefaultEnterprise(userId, enterpriseId),
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["platform", "users", vars.userId] }),
  });
}

export function useUpdateUserStorageLimit() {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, storageLimitBytes }: { userId: string; storageLimitBytes: number | null }) =>
      client.platform.updateUserStorageLimit(userId, storageLimitBytes),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["files"] }),
  });
}

export function useMyStorage() {
  const client = useApiClient();
  return useQuery({ queryKey: ["platform", "my-storage"], queryFn: () => client.platform.myStorage() });
}
